import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { Card, Input, Row, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { SorterResult, SortOrder } from "antd/lib/table/interface";
import { TableProps } from "antd/lib/table";
import { isEmpty, map } from "lodash";
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { DropdownInput } from "components";
import ResizableTitle from "components/ResizableTitle";
import useResizableTableHeader from "hooks/useResizableTableHeader";
import { getTotalDisplay } from "utils/getTotalDisplay";
import { AiccSubUser } from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { useAICCStore } from "../../useAICCStore";
import { PreviewLegend } from "./PreviewDot";
import useAiccLanguages from "../../useAiccLanguages";
import getColumns from "./columnConfig";
import styles from "./SubUserList.module.scss";

const PAGE_LIMIT = 10;

function SubUserList() {
  const history = useHistory();
  const { path } = useRouteMatch();
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, PAGE_LIMIT),
    sortKey: withDefault(StringParam, "last_active_at"),
    sortOrder: withDefault(StringParam, "descend"),
    languageIds: withDefault(ArrayParam, undefined),
    customerEmail: withDefault(StringParam, undefined),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [subUsers, setSubUsers] = useState<AiccSubUser[]>([]);
  const [total, setTotal] = useState(0);
  const [customerEmail, setCustomerEmail] = useState(query.customerEmail);
  const languages = useAiccLanguages();
  const setCachedSubUserDetail = useAICCStore((state) => state.setCachedSubUserDetail);
  const selectedLanguageIds = useMemo(() => map(query.languageIds, Number), [query.languageIds]);

  const loadSubUsers = useCallback(() => {
    setIsLoading(true);
    return aiccAPI
      .fetchAiccSubUsers({
        page: query.page,
        limit: query.limit,
        language_ids: isEmpty(selectedLanguageIds) ? undefined : selectedLanguageIds,
        customer_email: query.customerEmail,
        sort_key: query.sortKey,
        sort_order: query.sortOrder === "ascend" ? "asc" : "desc",
      })
      .then((response) => {
        setSubUsers(response.items);
        setTotal(response.total);
      })
      .finally(() => setIsLoading(false));
  }, [selectedLanguageIds, query.page, query.limit, query.customerEmail, query.sortKey, query.sortOrder]);

  useEffect(() => {
    loadSubUsers();
  }, [loadSubUsers]);

  useEffect(() => {
    setCustomerEmail(query.customerEmail);
  }, [query]);

  const languageOptions = useMemo(() => map(languages, ({ id, name }) => ({ label: name, value: id })), [languages]);

  const columns = useMemo(() => getColumns(query.sortKey, query.sortOrder as SortOrder), [
    query.sortKey,
    query.sortOrder,
  ]);

  const columnsToDisplay = useResizableTableHeader<AiccSubUser>(columns, 100, 500);

  const handleTableChange: TableProps<AiccSubUser>["onChange"] = (pagination, filters, sorter, extra) => {
    const { order, columnKey } = sorter as SorterResult<AiccSubUser>;
    if (extra.action === "sort") {
      setQuery({ ...query, page: 1, sortKey: columnKey as string, sortOrder: order as string });
    }
  };

  const handleRowClick: TableProps<AiccSubUser>["onRow"] = (record) => ({
    onClick: () => {
      setCachedSubUserDetail({
        id: record.id,
        name: record.name,
        customerId: record.customerId,
        customerEmail: record.customerEmail,
        studyLanguages: map(record.studyLanguages, ({ id, name }) => ({ id, name })),
        isPreview: record.isPreview,
      });
      history.push(`${path}/${record.id}`);
    },
  });

  const handleSearch = () => {
    setQuery({ ...query, page: 1, customerEmail: customerEmail?.trim() });
  };

  const handleLanguageChange = (languageIds: number[]) => {
    setQuery({ ...query, page: 1, languageIds: map(languageIds, String) });
  };

  return (
    <Card
      title="Subuser"
      className="page-container"
      style={{
        overflowX: "auto",
      }}
    >
      <Row className={styles.filters} justify="space-between" align="middle">
        <Space size={12}>
          <DropdownInput
            options={languageOptions}
            value={selectedLanguageIds}
            onChange={handleLanguageChange}
            placeholder="All languages"
          />
          <Input
            allowClear
            value={customerEmail}
            className={styles.searchInput}
            placeholder="Search Customer Email"
            prefix={<SearchOutlined />}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCustomerEmail(e.target.value)}
            onBlur={() => setCustomerEmail((prevState) => prevState?.trim())}
            onPressEnter={handleSearch}
          />
        </Space>
        <PreviewLegend />
      </Row>

      <Table<AiccSubUser>
        scroll={{ x: "100%" }}
        loading={isLoading}
        dataSource={subUsers}
        columns={columnsToDisplay}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        rowKey={(record) => record.id}
        showSorterTooltip={false}
        onChange={handleTableChange}
        onRow={handleRowClick}
        pagination={{
          showTotal: getTotalDisplay,
          current: query.page,
          pageSize: query.limit,
          total,
          defaultCurrent: 1,
          showSizeChanger: true,
          onChange: (page, pageSize) => setQuery({ ...query, page, limit: pageSize }),
        }}
      />
    </Card>
  );
}

export default SubUserList;
